import axios from "axios";
import { ChecklistConfig, Question, QuestionLocationMap, QuestionMap, Section, Subsection } from "../types/checklist";

const CHECKLIST_CDN_URL = "https://d26eu91dagpkvn.cloudfront.net";

// In-memory cache for checklist configs
const configCache: Map<string, ChecklistConfig> = new Map();
const questionMapCache: Map<string, QuestionMap> = new Map();
const questionLocationMapCache: Map<string, QuestionLocationMap> = new Map();

/**
 * Build a flat map of questionId -> Question for efficient lookup
 */
const buildQuestionMap = (config: ChecklistConfig): QuestionMap => {
    const questionMap: QuestionMap = {};

    config.sections.forEach((section: Section) => {
        section.subsections.forEach((subsection: Subsection) => {
            subsection.questions.forEach((question: Question) => {
                questionMap[question.id] = question;
            });
        });
    });

    return questionMap;
};

/**
 * Build a flat map of questionId -> QuestionLocation for section/subsection lookup
 */
const buildQuestionLocationMap = (config: ChecklistConfig): QuestionLocationMap => {
    const locationMap: QuestionLocationMap = {};

    config.sections.forEach((section: Section) => {
        section.subsections.forEach((subsection: Subsection) => {
            subsection.questions.forEach((question: Question) => {
                locationMap[question.id] = {
                    sectionId: section.id,
                    sectionTitle: section.title,
                    sectionOrder: section.order,
                    subsectionId: subsection.id,
                    subsectionTitle: subsection.title,
                };
            });
        });
    });

    return locationMap;
};

/**
 * Generate cache key for a specific checklist type and version
 */
const getCacheKey = (checklistType: string, version: string): string => {
    return `${checklistType}_${version}`;
};

/**
 * Fetch checklist configuration from CDN
 * @param checklistType - Type of checklist (e.g., 'GEARED_BULK_CARRIER')
 * @param version - Version of the checklist (default: 'latest')
 * @returns Promise<ChecklistConfig>
 */
export const fetchChecklistConfig = async (
    checklistType: string,
    version: string = "latest"
): Promise<ChecklistConfig> => {
    const cacheKey = getCacheKey(checklistType, version);

    // Check cache first
    const cached = configCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    try {
        const url = `${CHECKLIST_CDN_URL}/checklists/${checklistType}/${version}.json`;
        const response = await axios.get<ChecklistConfig>(url, {
            headers: {
                "Cache-Control": "no-cache",
            },
        });

        const config = response.data;

        // Cache the config and build maps
        configCache.set(cacheKey, config);
        questionMapCache.set(cacheKey, buildQuestionMap(config));
        questionLocationMapCache.set(cacheKey, buildQuestionLocationMap(config));

        return config;
    } catch (error) {
        console.error(`Failed to fetch checklist config for ${checklistType} v${version}:`, error);
        throw error;
    }
};

/**
 * Get the question map for a specific checklist
 * Must call fetchChecklistConfig first
 */
export const getQuestionMap = (checklistType: string, version: string = "latest"): QuestionMap | undefined => {
    const cacheKey = getCacheKey(checklistType, version);
    return questionMapCache.get(cacheKey);
};

/**
 * Get the question location map for a specific checklist
 * Must call fetchChecklistConfig first
 */
export const getQuestionLocationMap = (checklistType: string, version: string = "latest"): QuestionLocationMap | undefined => {
    const cacheKey = getCacheKey(checklistType, version);
    return questionLocationMapCache.get(cacheKey);
};

/**
 * Get a specific question by ID from a loaded checklist
 */
export const getQuestionById = (
    checklistType: string,
    version: string,
    questionId: string
): Question | undefined => {
    const questionMap = getQuestionMap(checklistType, version);
    return questionMap?.[questionId];
};

/**
 * Clear all cached checklist configs
 */
export const clearChecklistCache = (): void => {
    configCache.clear();
    questionMapCache.clear();
    questionLocationMapCache.clear();
};

/**
 * Check if a checklist config is already cached
 */
export const isChecklistCached = (checklistType: string, version: string = "latest"): boolean => {
    const cacheKey = getCacheKey(checklistType, version);
    return configCache.has(cacheKey);
};
